import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AuthModalManager } from './modals/AuthModalManager';
import { isNative } from '../services/nativeAuth';
import { useNavigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useTranslation('common');
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [initialModal, setInitialModal] = useState<'login' | 'signup'>('login');
  const [intendedDestination, setIntendedDestination] = useState<string | null>(null);

  // Check for signup query parameter and show modal if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      const params = new URLSearchParams(location.search);
      if (params.get('signup') === 'true') {
        setInitialModal('signup');
        setShowAuthModal(true);
        // Store the intended destination (without query params)
        setIntendedDestination(location.pathname);
      } else {
        // Show login modal for all protected routes
        setShowAuthModal(true);
        // Store intended destination for deep links
        if (location.pathname !== '/coach' || location.search) {
          setIntendedDestination(location.pathname + location.search);
        }
      }
    } else if (isAuthenticated) {
      // User is authenticated, make sure modal is closed
      setShowAuthModal(false);
    }
  }, [location.search, location.pathname, isAuthenticated, loading]);

  // Redirect to intended destination after successful login
  useEffect(() => {
    if (isAuthenticated) {
      if (intendedDestination) {
        navigate(intendedDestination, { replace: true });
        setIntendedDestination(null);
      } else if (location.pathname === '/coach' && !location.search) {
        // If on /coach without query params and just authenticated, ensure we're showing the page
        // This handles the case where user logged in from homepage
        // No navigation needed - just let the ProtectedRoute render children
      }
    }
  }, [isAuthenticated, intendedDestination, navigate, location.pathname, location.search]);

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hockey-blue mx-auto"></div>
          <p className="mt-2 text-gray-600">{t('common:state.loading')}</p>
        </div>
      </div>
    );
  }

  // Show login modal if not authenticated
  if (!isAuthenticated) {
    const handleModalClose = () => {
      // In de app is er geen sluitknop (zie dismissible hieronder), dus hier komt native
      // niet. Mocht dat ooit veranderen: '/' is daar geen uitweg maar een rondje, want
      // App.tsx stuurt dat meteen door naar /coach en dan staat dit venster er weer.
      if (isNative()) return;

      // User closed modal without logging in - redirect to homepage
      // Use window.location to force a full page reload to the static homepage
      window.location.href = '/';
    };

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hockey-blue mx-auto mb-2"></div>
          <p className="text-sm">{t('common:state.loading')}</p>
        </div>
        <AuthModalManager
          isOpen={showAuthModal}
          onClose={handleModalClose}
          initialModal={initialModal}
          dismissible={!isNative()}
        />
      </div>
    );
  }

  // User is authenticated, show protected content
  return <>{children}</>;
};